"use client";

import { useEffect, useState, useCallback } from "react";

type Status = "none" | "running" | "completed" | "failed";

interface ResearchStatusResponse {
  status: Status;
  download_url: string | null;
  error_message: string | null;
}

interface ResearchStatusProps {
  threadId: string | null;
}

export function ResearchStatus({ threadId }: ResearchStatusProps) {
  const [status, setStatus] = useState<Status>("none");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!threadId) return;

    try {
      const response = await fetch(`/api/threads/${threadId}/research`);
      if (response.ok) {
        const data: ResearchStatusResponse = await response.json();
        setStatus(data.status);
        setDownloadUrl(data.download_url);
        setError(data.error_message);
      }
    } catch (e) {
      console.error("Failed to fetch research status:", e);
    }
  }, [threadId]);

  useEffect(() => {
    if (!threadId) {
      setStatus("none");
      return;
    }

    // Initial fetch
    fetchStatus();

    // Poll every 10 seconds if running or none (to detect when research starts)
    const interval = setInterval(() => {
      if (status === "running" || status === "none") {
        fetchStatus();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [threadId, status, fetchStatus]);

  const handleDownload = async () => {
    if (!threadId) return;

    setIsDownloading(true);
    try {
      // Download ZIP file containing all documents (RFP and/or research report)
      const response = await fetch(
        `/api/threads/${threadId}/documents/download`
      );
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mars_documents_${threadId}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error("Download error:", e);
      alert("Failed to download documents");
    } finally {
      setIsDownloading(false);
    }
  };

  // Don't show anything if no research
  if (status === "none") return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 p-4 rounded-lg shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
      {status === "running" && (
        <div className="flex items-center gap-3">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="text-sm">Researching suppliers...</span>
        </div>
      )}

      {status === "completed" && (
        <div className="flex items-center gap-3">
          <svg
            className="h-5 w-5 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span className="text-sm">Research complete!</span>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="ml-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {isDownloading ? "Downloading..." : "Download ZIP"}
          </button>
        </div>
      )}

      {status === "failed" && (
        <div className="flex items-center gap-3">
          <svg
            className="h-5 w-5 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <div>
            <span className="text-sm text-red-500">Research failed</span>
            {error && <p className="text-xs text-zinc-500">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
